import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { getPlatformLogoSources, openPlatformTool } from '../utils/platformNavigation';
import { platformIconSvgDataUrl } from '../utils/platformLauncherUtils';
import { TOOLS_CACHE_URL, usePlatformAccessChanged } from '../utils/platformAccessSync';

function PlatformLauncherIcon({ tool }) {
  const sources = useMemo(() => getPlatformLogoSources(tool), [tool]);
  const fallback = useMemo(() => platformIconSvgDataUrl(tool), [tool]);
  const [src, setSrc] = useState(() => sources?.primary || fallback);

  useEffect(() => {
    setSrc(sources?.primary || fallback);
  }, [sources?.primary, fallback]);

  return (
    <img
      className="platform-launcher-item-icon-img"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => {
        if (src !== fallback) setSrc(fallback);
      }}
    />
  );
}

function PlatformLauncherItem({ tool, onSelect }) {
  return (
    <button type="button" className="platform-launcher-item" onClick={() => onSelect(tool)}>
      <span className="platform-launcher-item-icon">
        <PlatformLauncherIcon tool={tool} />
      </span>
      <span className="platform-launcher-item-label">{tool.name}</span>
    </button>
  );
}

export function PlatformLauncherWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rootRef = useRef(null);
  const cachedTools = getCachedJson(TOOLS_CACHE_URL);
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState(() => (Array.isArray(cachedTools?.tools) ? cachedTools.tools : []));
  const [toolsLoading, setToolsLoading] = useState(false);

  const loadTools = useCallback(async ({ force } = { force: false }) => {
    setToolsLoading(true);
    try {
      const toolsData = await fetchJsonCached(TOOLS_CACHE_URL, {
        ttlMs: force ? 0 : 15_000,
      });
      setTools(Array.isArray(toolsData?.tools) ? toolsData.tools : []);
    } catch {
      // keep cached values
    } finally {
      setToolsLoading(false);
    }
  }, []);

  usePlatformAccessChanged(useCallback(() => {
    void loadTools({ force: true });
  }, [loadTools]));

  useEffect(() => {
    if (!user) return undefined;
    void loadTools({ force: false });
    return undefined;
  }, [user, loadTools]);

  useEffect(() => {
    if (!open) return undefined;

    void loadTools({ force: true });
    return undefined;
  }, [open, loadTools]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const visibleTools = useMemo(() => tools.filter((tool) => tool && tool.key), [tools]);

  const onSelectTool = useCallback((tool) => {
    setOpen(false);
    openPlatformTool(tool, navigate);
  }, [navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className={`platform-launcher${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="platform-launcher-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Open platforms"
      >
        <span className="platform-launcher-trigger-inner" aria-hidden="true">
          <span className="platform-launcher-dot-grid">
            {Array.from({ length: 9 }).map((_, index) => (
              <span key={index} />
            ))}
          </span>
        </span>
      </button>

      {open && (
        <div className="platform-launcher-panel" role="dialog" aria-label="Platforms">
          <div className="platform-launcher-scroll">
            {toolsLoading && visibleTools.length === 0 ? (
              <p className="platform-launcher-empty">Loading platforms…</p>
            ) : visibleTools.length === 0 ? (
              <p className="platform-launcher-empty">No platforms available.</p>
            ) : (
              <div className="platform-launcher-grid">
                {visibleTools.map((tool) => (
                  <PlatformLauncherItem key={tool.key} tool={tool} onSelect={onSelectTool} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
